import React, { createContext, useContext, useEffect, useState } from "react";
import { API } from '../../../utils/api';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [userDetails, setUserDetail] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch user details and permissions from backend
  useEffect(() => {
    const fetchUserDetails = async () => {
      const token = localStorage.getItem("jmc_token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/user/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUserDetail({
            email: data.email,
            username: data.username,
            first_name: data.first_name,
            last_name: data.last_name,
            role: data.role,
            custom_role_id: data.custom_role_id,
            custom_role_name: data.custom_role_name
          });
          setPermissions(data.permissions || {});
        } else if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem("jmc_token");
          localStorage.removeItem("jmc_user");
          setUserDetail(null);
          setPermissions({});
        }
      } catch (err) {
        console.error("Failed to fetch user details:", err);
        // Try to load from localStorage as fallback
        const storedUser = localStorage.getItem("jmc_user");
        if (storedUser) {
          try {
            setUserDetail(JSON.parse(storedUser));
          } catch (parseErr) {
            console.error("Invalid user data in localStorage", parseErr);
            localStorage.removeItem("jmc_user");
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (userDetails) {
      localStorage.setItem("jmc_user", JSON.stringify(userDetails));
    } else {
      localStorage.removeItem("jmc_user");
    }
  }, [userDetails]);

  return (
    <UserContext.Provider value={{ userDetails, setUserDetail, permissions, loading }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to access user context easily
export const useUser = () => useContext(UserContext);
